import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  PageShell,
  type IconTint,
} from "@/components/page";
import {
  TableOfContents,
  type TableOfContentsItem,
} from "./TableOfContents";
import { EditorialDivider } from "./EditorialDivider";
import {
  EditorialFooter,
  type EditorialFooterProps,
} from "./EditorialFooter";
import { LivingBanner, type LivingBannerProps } from "./LivingBanner";

/**
 * Editorial page frame. Composes the existing <PageShell> (Hero + scroll
 * canvas) with the architecture-page-style editorial chrome:
 *
 *   PageShell hero
 *     ↓
 *   optional <LivingBanner>
 *     ↓
 *   <TableOfContents>
 *     ↓
 *   children (typically <EditorialMarkdownSection> separated by
 *   <EditorialDivider>)
 *     ↓
 *   <EditorialFooter>
 *
 * Drops in as a wrapper around any Lilach content page that wants the
 * full editorial treatment.
 */
export type EditorialPageFrameProps = {
  // Hero ───────────────────────────────────────────────────────────
  /** Optional small uppercase kicker above the title. */
  eyebrow?: string;
  /** Page title — h1. */
  title: string;
  /** Optional muted subtitle. */
  subtitle?: string;
  /** Optional Lucide icon in a colored hero badge. */
  icon?: LucideIcon;
  /** Tint for the hero badge. Default: rose to match editorial accent. */
  iconColor?: IconTint;
  /** Optional metadata row (date, author, status). */
  meta?: ReactNode;
  /** Optional actions. */
  actions?: ReactNode;
  /** Reading column max width. Default: max-w-3xl. */
  maxWidth?: "max-w-3xl" | "max-w-4xl" | "max-w-5xl";

  // Editorial chrome ───────────────────────────────────────────────
  /** TOC items. Empty list hides the TOC entirely. */
  toc?: TableOfContentsItem[];
  /** Optional TOC heading override. */
  tocHeading?: string;
  /** Optional living banner. */
  living?: LivingBannerProps;
  /** Footer config. Omit to hide the footer. */
  footer?: EditorialFooterProps;

  // Body ──────────────────────────────────────────────────────────
  /** Section content — ideally a list of <EditorialMarkdownSection>
   *  separated by <EditorialDivider>. */
  children: ReactNode;

  /** Extra classes on the outer wrapper. */
  className?: string;
};

export function EditorialPageFrame({
  eyebrow,
  title,
  subtitle,
  icon,
  iconColor = "rose",
  meta,
  actions,
  maxWidth = "max-w-3xl",
  toc,
  tocHeading,
  living,
  footer,
  children,
  className,
}: EditorialPageFrameProps) {
  return (
    <PageShell
      eyebrow={eyebrow}
      title={title}
      subtitle={subtitle}
      icon={icon}
      iconColor={iconColor}
      meta={meta}
      actions={actions}
      maxWidth={maxWidth}
      className={className}
    >
      {living ? <LivingBanner {...living} /> : null}

      {toc && toc.length > 0 ? (
        <TableOfContents heading={tocHeading} items={toc} />
      ) : null}

      <div className={cn("space-y-12")}>{children}</div>

      {footer ? (
        <EditorialFooter
          primary={footer.primary}
          secondary={footer.secondary}
        />
      ) : null}
    </PageShell>
  );
}

export { EditorialDivider };
